import React, { useMemo, useState } from "react";

import {
  faCalculatorSimple,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import {
  CellContext,
  createColumnHelper,
  RowData,
} from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { HighlightedText } from "src/base-components/HighlightedText";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { FeatureActionsMenu } from "src/featureCatalogue/Features/FeatureActionsMenu";
import {
  EntityFilter,
  EventTypeFilter,
  StatusFilter,
} from "src/featureCatalogue/Features/FilterActions";
import { ManageQueryModal } from "src/featureCatalogue/Queries/ManageQueryModal";
import { QueryStatusPill } from "src/featureCatalogue/Queries/QueryStatusPill";
import { useFeatures } from "src/featureCatalogue/queries";
import { useFilteredData } from "src/featureCatalogue/useFilteredData";
import {
  commonColumns,
  EntityPills,
  Feature,
  FilterOptions,
  getQueryEditPermissions,
  mapFeatureQueryToQuery,
  Query,
  statusColumns,
} from "src/featureCatalogue/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { getTypeIcon } from "src/utils/icons";
import { speakPythonPrimitive } from "src/utils/speakPython";

const columnHelper = createColumnHelper<Feature>();

const HighlightedCellText = <D extends RowData>({
  info,
}: {
  info: CellContext<D, string | null | undefined>;
}) => (
  <HighlightedText
    highlight={info.table.getState().globalFilter}
    highlightClassName="font-inter-semibold-12px bg-yellow-200"
    text={info.getValue()}
  />
);

// Shared column definitions
const nameColumn = (headerText: string, options?: { sticky?: "left" }) =>
  columnHelper.accessor("name", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => {
      const name = info.getValue();
      return (
        <Cell className="gap-x-0.5" info={info}>
          <Icon
            icon={
              info.row.original.feature_type
                ? getTypeIcon(info.row.original.feature_type, faMinus)
                : faMinus
            }
            size="xs"
          />
          <Tooltip placement="top" title={name} asChild>
            <div className="truncate">
              <HighlightedCellText info={info} />
            </div>
          </Tooltip>
        </Cell>
      );
    },
    maxSize: 300,
    minSize: 300,
    filterFn: "includesString",
    meta: options?.sticky ? { sticky: options.sticky } : undefined,
  });

const keyColumn = (headerText: string) =>
  columnHelper.accessor("key", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Text>
            <HighlightedCellText info={info} />
          </Pill.Text>
        </Pill>
      </Cell>
    ),
    maxSize: 300,
    minSize: 300,
    filterFn: "includesString",
  });

const descriptionColumn = () =>
  columnHelper.accessor("description", {
    header: (info) => <Header info={info}>Description</Header>,
    cell: (info) => (
      <DisplayDescriptionCell info={info} value={info.getValue()} />
    ),
    maxSize: 200,
    minSize: 200,
    filterFn: "includesString",
  });

const entityTypesColumn = (entityTypeOptions?: string[]) => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  return columnHelper.accessor("entity_types", {
    header: (info) => (
      <Header info={info}>
        <div
          className={twJoin(
            "flex items-center",
            featureLifecycleEnabled ? "gap-x-2" : "gap-x-4",
          )}
        >
          <span>Entity</span>
          <EntityFilter column={info.column} options={entityTypeOptions} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell className="flex space-x-2" info={info}>
        <EntityPills entities={info.getValue() ?? []} />
      </Cell>
    ),
    minSize: 150,
    maxSize: 150,
    filterFn: "includesString",
  });
};

const featureDefaultColumn = () =>
  columnHelper.accessor("feature_default", {
    header: (info) => (
      <Header info={info}>
        <span className="whitespace-nowrap">Default Value</span>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>{speakPythonPrimitive(info.getValue() ?? "None")}</Cell>
    ),
    minSize: 130,
    maxSize: 130,
  });

const actionsMenuColumn = (options?: { sticky?: "right" }) =>
  columnHelper.display({
    id: "actions-menu",
    header: () => null,
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <FeatureActionsMenu feature={info.row.original} />
      </Cell>
    ),
    enableGlobalFilter: false,
    meta: options?.sticky ? { sticky: options.sticky } : undefined,
  });

const getFeatureColumns = (filterOptions: FilterOptions) => [
  nameColumn("Feature", { sticky: "left" }),
  keyColumn("Feature Key"),
  descriptionColumn(),
  entityTypesColumn(filterOptions.entityTypes),
  // Event column
  columnHelper.accessor("query.event" as keyof Feature, {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-2">
          <span>Event</span>
          <EventTypeFilter options={filterOptions.eventTypes} />
        </div>
      </Header>
    ),
    cell: (info) => {
      const eventColumn = commonColumns<Feature>().find(
        (col) => col.accessorKey === "event",
      );
      return info.row.original.query && eventColumn
        ? eventColumn.cell(info)
        : null;
    },
    minSize: 200,
    maxSize: 200,
  }),
  ...statusColumns(columnHelper, {
    getEnvironmentSettings: (row) => row.query?.environment_settings,
    hasQuery: (row) => !!row.query?.id,
    getQueryId: (row) => row.query?.id ?? "",
    getEtag: (row) => row.query?.etag ?? "",
    getEntities: (row) => row.entity_types ?? [],
    getEvent: (row) => row.query?.event ?? "",
    getFeatures: (row) => row.query?.columns?.features ?? [],
  }),
  ...commonColumns<Feature>()
    .filter((column) => column.accessorKey !== "event")
    .map((column) =>
      columnHelper.accessor(`query.${column.accessorKey}` as keyof Feature, {
        header: (info) => column.header(info),
        cell: (info) => (info.row.original.query ? column.cell(info) : null),
        minSize: column.minSize,
        maxSize: column.maxSize,
        enableGlobalFilter: false,
      }),
    ),
  featureDefaultColumn(),
  columnHelper.accessor(
    (row) => row.query?.query_name || "Untitled Feature Query",
    {
      id: "query_name",
      header: (info) => <Header info={info}>Query Name</Header>,
      cell: (info) => (
        <Cell className="gap-x-0.5" info={info}>
          <Tooltip placement="left" title={info.getValue()} asChild>
            <div className="truncate">
              <HighlightedCellText info={info} />
            </div>
          </Tooltip>
        </Cell>
      ),
      minSize: 150,
      maxSize: 150,
    },
  ),
  actionsMenuColumn({ sticky: "right" }),
];

const getColumns = (env: string) => [
  nameColumn("Feature name"),
  descriptionColumn(),
  keyColumn("Key"),
  columnHelper.accessor(
    (row) => {
      return row.query?.environment_settings?.[env]?.status;
    },
    {
      id: "environment_settings_status",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            Status
            <StatusFilter column={info.column} />
          </div>
        </Header>
      ),
      cell: (info) => {
        const currentStatus = info.getValue();

        return currentStatus ? (
          <Cell info={info}>
            <QueryStatusPill status={currentStatus} />
          </Cell>
        ) : null;
      },
      enableGlobalFilter: false,
      filterFn: "equals",
    },
  ),
  featureDefaultColumn(),
  entityTypesColumn(),
  ...commonColumns<Feature>().map((column) =>
    columnHelper.accessor(`query.${column.accessorKey}` as keyof Feature, {
      header: (info) =>
        column.accessorKey === "event" ? (
          <Header info={info}>
            <div className="flex items-center gap-x-2">
              Event
              <EventTypeFilter column={info.column} />
            </div>
          </Header>
        ) : (
          column.header(info)
        ),
      cell: (info) => (info.row.original.query ? column.cell(info) : null),
      enableGlobalFilter: false,
    }),
  ),
  actionsMenuColumn(),
];

const FEATURE_FILTER_CONFIG = {
  entityFilterKey: "filter[entity_types][eq]",
  getEntityValues: (f: Feature) => f.entity_types ?? [],
  getEvent: (f: Feature) => f.query?.event ?? "",
  getSandboxStatus: (f: Feature) =>
    f.query?.environment_settings?.sandbox?.status,
  getLiveStatus: (f: Feature) => f.query?.environment_settings?.live?.status,
};

export const Features: React.FC<{
  openCreateModal: () => void;
  searchQuery: string;
  resetSearch: () => void;
}> = ({ openCreateModal, searchQuery, resetSearch }) => {
  const [environment] = useEnvironment();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const { featureQueriesCatalogue } = useCapabilities();
  const { data: features, isLoading, isSuccess } = useFeatures({ environment });
  const {
    openModal,
    closeModal,
    afterLeave,
    isOpen,
    data: queryToEdit,
  } = useModal<Query>();

  const { readOnly: isQueryReadOnly, readOnlyReason: queryReadOnlyReason } =
    featureLifecycleEnabled
      ? getQueryEditPermissions(queryToEdit, featureQueriesCatalogue)
      : { readOnly: !!queryToEdit?.finalized_at, readOnlyReason: undefined };

  const {
    filteredItems: filteredFeatures,
    hasActiveFilters,
    resetFilters,
  } = useFilteredData({
    items: features ?? [],
    filterConfig: FEATURE_FILTER_CONFIG,
  });

  const filterOptions = useMemo(() => {
    const entityTypes = [
      ...new Set(features?.flatMap((f) => f.entity_types ?? []) ?? []),
    ];
    const eventTypes = [
      ...new Set(
        features?.map((f) => f.query?.event).filter((e): e is string => !!e) ??
          [],
      ),
    ];
    return { entityTypes, eventTypes };
  }, [features]);

  const columns = useMemo(() => {
    if (featureLifecycleEnabled) {
      return getFeatureColumns(filterOptions);
    }
    return getColumns(environment);
  }, [environment, featureLifecycleEnabled, filterOptions]);

  const emptyStateNode = (
    <EmptyState
      actionButton={{
        icon: faPlus,
        text: "Create Feature",
        onClick: () => {
          openCreateModal();
        },
      }}
      dataLoc="features-empty-state"
      description="Features help you detect patterns and analyze behavior across your entities and events."
      headline="No features configured yet"
      icon={faCalculatorSimple}
    />
  );

  const filterEmptyStateNode = (
    <EmptyState
      actionButton={
        hasActiveFilters || searchQuery
          ? {
              text: "Clear filters",
              onClick: () => {
                resetFilters();
                resetSearch();
              },
            }
          : undefined
      }
      description="Try adjusting your search or filters to find what you're looking for"
      headline="No matching features found"
      icon={faSearch}
    />
  );

  const showEmptyState = !isLoading && isSuccess && !filteredFeatures.length;
  const isFilteredEmptyState =
    showEmptyState && (hasActiveFilters || !!searchQuery);

  return (
    <>
      <WhitePane>
        <div className="flex h-full w-full flex-col gap-y-4">
          {isSuccess && features?.length === 0 ? (
            emptyStateNode
          ) : (
            <div
              className={twJoin(
                featureLifecycleEnabled &&
                  "relative flex min-h-0 flex-1 flex-col p-4",
                featureLifecycleEnabled &&
                  showEmptyState &&
                  "items-center justify-center",
              )}
            >
              <TableComp
                columns={columns}
                data={filteredFeatures}
                dataLoc="features-list"
                emptyState={filteredFeatures.length > 0 && filterEmptyStateNode}
                frameClassName={twJoin(
                  "w-full",
                  featureLifecycleEnabled ? "min-h-0 flex-1" : "p-4",
                )}
                globalFilter={searchQuery}
                initialState={{
                  sorting: featureLifecycleEnabled
                    ? [
                        { id: "query_window_length", desc: false },
                        { id: "query_window_hop", desc: false },
                      ]
                    : [{ id: "key", desc: true }],
                }}
                isLoading={isLoading}
                rowPropsGetter={(row) => {
                  const hasQuery =
                    featureLifecycleEnabled && !!row.original.query?.id;
                  return {
                    "data-loc": row.original.key,
                    ...(hasQuery && {
                      onClick: () => {
                        const query = mapFeatureQueryToQuery(
                          row.original.query,
                        );
                        if (query) {
                          openModal(query);
                        }
                      },
                    }),
                    classNameOverrides: twJoin(
                      hasQuery &&
                        "cursor-pointer hover:bg-gray-50 [&_.sticky]:hover:bg-gray-50",
                    ),
                  };
                }}
                variant={featureLifecycleEnabled ? "compact" : "default"}
                clientSideFiltering
              />
              {featureLifecycleEnabled &&
                showEmptyState &&
                (isFilteredEmptyState ? filterEmptyStateNode : emptyStateNode)}
            </div>
          )}
        </div>
      </WhitePane>
      <ManageQueryModal
        afterLeave={afterLeave}
        open={isOpen}
        query={queryToEdit}
        readOnly={isQueryReadOnly}
        readOnlyReason={queryReadOnlyReason}
        onClose={closeModal}
      />
    </>
  );
};

const getFirstLineContent = (descriptionValue: string) => {
  if (descriptionValue.includes("\n")) {
    return `${descriptionValue.split("\n")[0]}...`;
  }
  return descriptionValue.length > 50
    ? `${descriptionValue.slice(0, 43)}...`
    : descriptionValue;
};

export const DisplayDescriptionCell = <D extends object, V>({
  info,
  value: descriptionValue,
}: {
  info: CellContext<D, V>;
  value: string | null | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const needsPopover =
    descriptionValue &&
    (descriptionValue.length > 50 || descriptionValue.includes("\n"));

  const firstLineContent = getFirstLineContent(descriptionValue ?? "");

  const content = (
    <div className="line-clamp-1 overflow-hidden">
      <div
        //skipcq: JS-0440
        dangerouslySetInnerHTML={{
          __html: mdService.toHtml(
            needsPopover ? firstLineContent : (descriptionValue ?? ""),
          ),
        }}
        className="review-case-description [&>p]:m-0"
      />
    </div>
  );

  return needsPopover ? (
    <CustomPopover
      button={<Cell info={info}>{content}</Cell>}
      className="w-96 overflow-auto border-0"
      isOpen={isOpen}
      offsetX={0}
      placement="top"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen && (
        <div
          //skipcq: JS-0440
          dangerouslySetInnerHTML={{
            __html: mdService.toHtml(descriptionValue),
          }}
          className="review-case-description p-3"
        />
      )}
    </CustomPopover>
  ) : (
    <Cell info={info}>{content}</Cell>
  );
};
